import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { User, CheckCircle, Lock, Loader2, AlertCircle } from 'lucide-react';
import { trackProfileCompleted, trackPageVisit } from '@/utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompleteProfile() {
  const [username, setUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [agreedToMonitoring, setAgreedToMonitoring] = useState(false); // MUST be false by default - user must explicitly agree
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();

  // Prevent back button bypass
  useEffect(() => {
    // Push a dummy state to prevent back navigation
    window.history.pushState(null, '', window.location.href);
    
    const handlePopState = (e) => {
      // Push state again to prevent going back
      window.history.pushState(null, '', window.location.href);
      toast.error('Please complete your profile first');
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentUser(response.data);
        
        // If user already has username and agreed to monitoring, redirect to dashboard
        const hasUsername = response.data.username && response.data.username.trim() !== '';
        if (hasUsername && response.data.agreed_to_monitoring) {
          // Check for pending invite
          const pendingInviteRef = localStorage.getItem('pendingInviteRef');
          if (pendingInviteRef) {
            localStorage.removeItem('pendingInviteRef');
            try {
              const inviteResponse = await axios.post(
                `${API}/invite/${pendingInviteRef}/accept`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              );
              navigate(`/chat/${inviteResponse.data.conversation_id}`);
            } catch (error) {
              navigate('/dashboard');
            }
          } else {
            navigate('/dashboard');
          }
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        // Only logout if token is invalid (401)
        // Don't logout on other errors - could be network issues
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        } else {
          toast.error('Failed to load profile. Please try again.');
        }
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchUser();
  }, [navigate]);

  // Check username availability with debounce
  useEffect(() => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingUsername(true);
      try {
        const response = await axios.get(`${API}/auth/check-username/${username}`);
        setUsernameAvailable(response.data.available);
      } catch (error) {
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [username]);

  const completeProfile = async () => {
    // Validate username
    if (!username || username.trim().length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    
    // Block invalid usernames like "no username", "-", empty, etc.
    const trimmedUsername = username.trim().toLowerCase();
    const invalidUsernames = ['no username', 'nousername', '-', 'n/a', 'na', 'none', 'null', 'undefined', 'test', 'user', 'admin'];
    if (invalidUsernames.includes(trimmedUsername) || trimmedUsername.length < 3) {
      toast.error('Please enter a valid username');
      return;
    }

    if (usernameAvailable === false) {
      toast.error('This username is already taken');
      return;
    }
    
    // CRITICAL: User MUST agree to terms and conditions
    if (!agreedToMonitoring) {
      toast.error('You must accept the terms and conditions to continue');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/complete-profile`,
        { 
          username, 
          agreed_to_monitoring: agreedToMonitoring 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Welcome to Privluxe!');
      
      // Track profile completion
      trackProfileCompleted(username);
      
      // Check for pending invite redirect
      const pendingInviteRef = localStorage.getItem('pendingInviteRef');
      if (pendingInviteRef) {
        localStorage.removeItem('pendingInviteRef');
        try {
          const response = await axios.post(
            `${API}/invite/${pendingInviteRef}/accept`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          navigate(`/chat/${response.data.conversation_id}`);
        } catch (error) {
          console.error('Invite join failed:', error);
          navigate('/invite-friend');
        }
      } else {
        // New users go to invite friend page
        navigate('/invite-friend');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && username && usernameAvailable && agreedToMonitoring) {
      completeProfile();
    }
  };

  if (initialLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #020617 100%)' }}
      >
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #0A0F2C 0%, #020617 100%)' }}
    >
      {/* Purple glow */}
      <div 
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)' }}
      />
      
      <Card 
        className="w-full max-w-md border-0 relative z-10"
        style={{ 
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)'
        }}
        data-testid="complete-profile-card"
      >
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div 
              className="p-3 rounded-full"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
            >
              <User className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold" style={{ color: '#E5E7EB' }}>
            Create Your Identity
          </CardTitle>
          <CardDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
            Choose a username for your private communications
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-5 pt-4">
          {/* Username Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2" style={{ color: '#E5E7EB' }}>
              <User className="h-4 w-4" style={{ color: 'rgba(229, 231, 235, 0.5)' }} />
              Username
            </label>
            <div className="relative">
              <Input
                type="text"
                placeholder="Choose a unique username"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                onKeyDown={handleKeyDown}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500 h-12 pr-10"
                data-testid="username-input"
                maxLength={20}
              />
              {/* Status indicator */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checkingUsername && (
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                )}
                {!checkingUsername && usernameAvailable === true && (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                )}
                {!checkingUsername && usernameAvailable === false && (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                )}
              </div>
            </div>
            
            {/* Username feedback */}
            <div className="h-5">
              {username && username.length < 3 && (
                <p className="text-xs text-gray-400">Minimum 3 characters</p>
              )}
              {!checkingUsername && usernameAvailable === true && (
                <p className="text-xs text-green-400">Username is available!</p>
              )}
              {!checkingUsername && usernameAvailable === false && (
                <p className="text-xs text-red-400">Username is already taken</p>
              )}
            </div>
            
            <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
              Lowercase letters, numbers, and underscores only
            </p>
          </div>

          {/* Terms acceptance checkbox */}
          <div 
            className="flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors hover:bg-white/5"
            style={{ background: 'rgba(255, 255, 255, 0.02)' }}
            onClick={() => setAgreedToMonitoring(!agreedToMonitoring)}
          >
            <div 
              className="w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5 transition-colors"
              style={{ 
                background: agreedToMonitoring ? 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' : 'rgba(255, 255, 255, 0.1)',
                border: agreedToMonitoring ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              {agreedToMonitoring && <CheckCircle className="h-3 w-3 text-white" />}
            </div>
            <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
              I accept the{' '}
              <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="underline hover:text-white transition-colors"
                style={{ color: '#22D3EE' }}
              >
                Terms and Conditions
              </a>
              {' '}and{' '}
              <a 
                href="/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="underline hover:text-white transition-colors"
                style={{ color: '#22D3EE' }}
              >
                Privacy Policy
              </a>
            </p>
          </div>

          <Button 
            onClick={completeProfile} 
            className="w-full h-12 text-white font-semibold"
            style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
            disabled={loading || !agreedToMonitoring || !username || username.length < 3 || usernameAvailable === false}
            data-testid="complete-button"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Enter Privluxe'
            )}
          </Button>

          <div className="flex items-center justify-center gap-2 text-xs" style={{ color: 'rgba(229, 231, 235, 0.3)' }}>
            <Lock className="h-3 w-3" />
            Your data is encrypted and secure
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
